import { z } from 'zod';
import {
  APPROVAL_REQUEST_STATUSES,
  APPROVAL_STEP_STATUSES,
  APPROVAL_TYPES,
  APPROVER_RESOLVERS,
  TASK_PRIORITIES,
  TASK_STATUSES,
  WORK_REPORT_STATUSES,
} from '../enums.js';
import {
  dateRangeQuery,
  dayString,
  departmentRef,
  employeeRef,
  isoDateTime,
  listQueryWith,
  nonEmpty,
  objectId,
  richText,
  scopeQuery,
  timestamps,
} from './common.js';

/** Approvals, tasks and daily work reports. */

// ── Approvals ───────────────────────────────────────────────────────────────────

export const approvalStep = z.object({
  id: objectId,
  stageIndex: z.number().int(),
  resolverType: z.enum(APPROVER_RESOLVERS),
  assignedApprover: employeeRef.nullable(),
  assignedRole: z.string().nullable(),
  status: z.enum(APPROVAL_STEP_STATUSES),
  decidedBy: employeeRef.nullable(),
  decidedAt: isoDateTime.nullable(),
  comment: z.string().nullable(),
  /** Reserved; delegation and escalation are deferred (Q-12). */
  delegatedFrom: employeeRef.nullable(),
  escalatedFromStageIndex: z.number().int().nullable(),
});

export const approvalRequest = z
  .object({
    id: objectId,
    type: z.enum(APPROVAL_TYPES),
    subjectType: z.string(),
    subjectId: objectId,
    requestedBy: employeeRef,
    status: z.enum(APPROVAL_REQUEST_STATUSES),
    currentStageIndex: z.number().int(),
    steps: z.array(approvalStep),
    /** Enough to render the inbox row without fetching the subject. */
    payloadSummary: z.record(z.string(), z.unknown()),
    completedAt: isoDateTime.nullable(),
    /** Optimistic concurrency: two simultaneous decisions cannot both commit. */
    version: z.number().int(),
  })
  .merge(timestamps);

export const approvalListQuery = listQueryWith({
  type: z.enum(APPROVAL_TYPES).optional(),
  status: z.enum(APPROVAL_REQUEST_STATUSES).optional(),
  assignedToMe: z.coerce.boolean().optional(),
});

export const decideApprovalBody = z.object({
  decision: z.enum(['approve', 'reject']),
  comment: z.string().max(1000).optional(),
  /** Must match the request's current version, or the decision is rejected as stale. */
  version: z.number().int(),
});

/**
 * An override is a DISTINCT, separately audited action — never silent participation in
 * the chain. An elevated role is not an implicit approver.
 */
export const overrideApprovalBody = z.object({
  decision: z.enum(['approve', 'reject']),
  reason: nonEmpty('Override reason', 1000),
  version: z.number().int(),
});

export const reassignApprovalBody = z.object({
  stageIndex: z.number().int(),
  toEmployeeId: objectId,
  reason: nonEmpty('Reason', 500),
});

/** A chain is configuration, not code. Changing it never alters in-flight requests. */
export const approvalStageConfig = z.object({
  resolver: z.enum(APPROVER_RESOLVERS),
  role: z.string().optional(),
  namedEmployeeId: objectId.optional(),
  required: z.boolean().default(true),
  /** e.g. `days > 5`; evaluated server-side against the subject. */
  condition: z.string().max(200).optional(),
  notifyOnly: z.boolean().default(false),
});

export const approvalChainConfig = z.object({
  type: z.enum(APPROVAL_TYPES),
  stages: z.array(approvalStageConfig).min(1),
});

export const approvalChainsBody = z.object({
  chains: z.array(approvalChainConfig),
});

// ── Tasks ───────────────────────────────────────────────────────────────────────

export const taskComment = z.object({
  id: objectId,
  author: employeeRef,
  bodyHtml: z.string(),
  createdAt: isoDateTime,
});

export const task = z
  .object({
    id: objectId,
    title: z.string(),
    description: z.string().nullable(),
    assignee: employeeRef.nullable(),
    createdBy: employeeRef,
    department: departmentRef.nullable(),
    status: z.enum(TASK_STATUSES),
    priority: z.enum(TASK_PRIORITIES),
    dueDate: dayString.nullable(),
    completedAt: isoDateTime.nullable(),
    /** DERIVED from dueDate and status at read time — never a stored flag that goes stale. */
    isOverdue: z.boolean(),
    commentCount: z.number().int(),
    attachmentFileIds: z.array(objectId),
  })
  .merge(timestamps);

export const createTaskBody = z.object({
  title: nonEmpty('Title', 200),
  description: z.string().max(5000).optional(),
  assigneeEmployeeId: objectId,
  priority: z.enum(TASK_PRIORITIES).default('medium'),
  dueDate: dayString.optional(),
  departmentId: objectId.optional(),
});

export const updateTaskBody = z
  .object({
    title: nonEmpty('Title', 200),
    description: z.string().max(5000).nullable(),
    status: z.enum(TASK_STATUSES),
    priority: z.enum(TASK_PRIORITIES),
    dueDate: dayString.nullable(),
  })
  .partial();

export const reassignTaskBody = z.object({
  assigneeEmployeeId: objectId,
  reason: z.string().max(500).optional(),
});

export const createTaskCommentBody = z.object({
  body: nonEmpty('Comment', 5000),
});

export const taskListQuery = listQueryWith({
  ...scopeQuery.shape,
  ...dateRangeQuery.shape,
  status: z.enum(TASK_STATUSES).optional(),
  priority: z.enum(TASK_PRIORITIES).optional(),
  assigneeEmployeeId: objectId.optional(),
  overdue: z.coerce.boolean().optional(),
});

export const taskBoardColumn = z.object({
  status: z.enum(TASK_STATUSES),
  total: z.number().int(),
  tasks: z.array(task),
});

// ── Daily work reports ──────────────────────────────────────────────────────────

export const workReportItem = z.object({
  description: nonEmpty('Description', 500),
  /** Optional by design — forcing a task link makes people invent tasks (Q-14). */
  taskId: objectId.nullable(),
  hours: z.number().min(0).max(24),
  projectName: z.string().max(120).nullable(),
});

export const workReportRevision = z.object({
  revision: z.number().int(),
  author: employeeRef,
  reason: z.string().nullable(),
  createdAt: isoDateTime,
});

export const workReport = z
  .object({
    id: objectId,
    employee: employeeRef,
    reportDate: dayString,
    status: z.enum(WORK_REPORT_STATUSES),
    items: z.array(workReportItem),
    workInProgressHtml: z.string().nullable(),
    blockersHtml: z.string().nullable(),
    nextDayPlanHtml: z.string().nullable(),
    attachmentFileIds: z.array(objectId),
    /**
     * The reviewing manager and department AS AT SUBMISSION, so a later reassignment
     * does not rewrite history. Current ACCESS still resolves against the current
     * hierarchy.
     */
    reviewerSnapshot: z.object({
      manager: employeeRef.nullable(),
      department: departmentRef.nullable(),
    }),
    approvalRequestId: objectId.nullable(),
    currentRevision: z.number().int(),
    revisions: z.array(workReportRevision),
    submittedAt: isoDateTime.nullable(),
    reviewedAt: isoDateTime.nullable(),
  })
  .merge(timestamps);

export const createWorkReportBody = z.object({
  reportDate: dayString,
  items: z.array(workReportItem).min(1, 'Add at least one item'),
  workInProgressHtml: richText.optional(),
  blockersHtml: richText.optional(),
  nextDayPlanHtml: richText.optional(),
  attachmentFileIds: z.array(objectId).default([]),
});

export const updateWorkReportBody = createWorkReportBody.partial().omit({ reportDate: true });

export const workReportListQuery = listQueryWith({
  ...scopeQuery.shape,
  ...dateRangeQuery.shape,
  status: z.enum(WORK_REPORT_STATUSES).optional(),
  employeeId: objectId.optional(),
});
